import { z } from 'zod';

/**
 * What a document is for. The three required purposes each own a slot on the
 * documents step; everything else is an extra attachment. Intake maps these
 * onto its own document types, so the purpose is what fills the Listing
 * Partner's checklist.
 */
const OFFER_DOCUMENT_PURPOSES = [
  'PURCHASE_AGREEMENT',
  'ABA_DISCLOSURE',
  'OPENDOOR_ADDENDUM',
  'PROOF_OF_FUNDS',
  'PRE_APPROVAL_LETTER',
  'OTHER',
] as const;

export type OfferDocumentPurpose = (typeof OFFER_DOCUMENT_PURPOSES)[number];

/**
 * Whether the submitting agent belongs to an MLS. It decides which agent
 * identifiers the offer must carry, and how the agent is credited on the sale.
 */
export const AGENT_TYPES = ['mls', 'non_mls'] as const;

/**
 * One uploaded document, as the draft carries it: the opaque Zeus document
 * handle plus the display metadata the wizard shows. Never the file bytes —
 * those live in S3 the moment the upload finishes.
 */
const offerDocumentRefSchema = z.object({
  documentId: z.string().min(1),
  fileName: z.string().min(1),
  sizeBytes: z.number().int().min(0),
  // Drafts written before the slots existed carry untyped attachments.
  purpose: z.enum(OFFER_DOCUMENT_PURPOSES).default('OTHER'),
});

export type OfferDocumentRef = z.infer<typeof offerDocumentRefSchema>;

/**
 * The in-progress offer, carried between wizard steps in sessionStorage — the
 * same handoff pattern as the typed address, and deliberately not the URL. All
 * money is integer cents; compensation is basis points.
 */
const offerDraftSchema = z.object({
  slug: z.string(),
  priceCents: z.number().int().nullable(),
  sellerConcessionCents: z.number().int().nullable(),
  buyerAgentCompensationBps: z.number().int().nullable(),
  earnestDepositCents: z.number().int().nullable().default(null),
  // Step 2 — the strict-intake contingency periods, in days; 0 means waived.
  inspectionPeriodDays: z.number().int().nullable().default(null),
  appraisalPeriodDays: z.number().int().nullable().default(null),
  financingPeriodDays: z.number().int().nullable().default(null),
  // Loan financing only; intake rejects them on cash / unknown financing.
  downPaymentCents: z.number().int().nullable().default(null),
  lenderType: z.string().nullable().default(null),
  // Step 2 — the legacy financing_type / close_of_escrow / contingent terms.
  financingType: z.string().nullable().default(null),
  closeDate: z.string().nullable().default(null),
  contingentOnSale: z.boolean().nullable().default(null),
  // The legacy contingent_offer_status values: listed | unlisted | in_contract.
  contingentHomeStatus: z.string().nullable().default(null),
  // Step 3 — buyer details for document generation.
  buyerFullName: z.string().nullable().default(null),
  buyerEmail: z.string().nullable().default(null),
  // Step 4 — the submitting agent.
  agentFirstName: z.string().nullable().default(null),
  agentLastName: z.string().nullable().default(null),
  agentEmail: z.string().nullable().default(null),
  agentPhone: z.string().nullable().default(null),
  agentLicenseNumber: z.string().nullable().default(null),
  // Step 4 — whether the agent is an MLS member, and the identifiers that
  // answer implies. `mls` matches the legacy portal's default selection.
  agentType: z.enum(AGENT_TYPES).default('mls'),
  agentMlsId: z.string().nullable().default(null),
  agentMlsOfficeId: z.string().nullable().default(null),
  // Step 5 — uploaded document references (handles + display metadata only).
  documents: z.array(offerDocumentRefSchema).default([]),
  // Groups one wizard session's uploads under one S3 prefix, server-minted.
  documentsSubmissionId: z.string().nullable().default(null),
});

export type OfferDraft = z.infer<typeof offerDraftSchema>;

const KEY = 'opendoor.makeOffer.offerDraft';

const storeOfferDraft = (draft: OfferDraft): void => {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(draft));
  } catch {
    // Storage can be unavailable; the next step re-asks rather than failing.
  }
};

export const readOfferDraft = (slug: string): OfferDraft | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) {
      return undefined;
    }
    const draft = offerDraftSchema.parse(JSON.parse(raw));
    // A draft for a different home must not leak into this one.
    return draft.slug === slug ? draft : undefined;
  } catch {
    return undefined;
  }
};

/** Drop the draft — a submitted offer must not leak into the next one. */
export const clearOfferDraft = (): void => {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Storage can be unavailable; nothing to clear then.
  }
};

/** Merge a step's fields into the stored draft. */
export const updateOfferDraft = (slug: string, partial: Partial<OfferDraft>): void => {
  const current = readOfferDraft(slug);
  storeOfferDraft({
    slug,
    priceCents: null,
    sellerConcessionCents: null,
    buyerAgentCompensationBps: null,
    earnestDepositCents: null,
    inspectionPeriodDays: null,
    appraisalPeriodDays: null,
    financingPeriodDays: null,
    downPaymentCents: null,
    lenderType: null,
    financingType: null,
    closeDate: null,
    contingentOnSale: null,
    contingentHomeStatus: null,
    buyerFullName: null,
    buyerEmail: null,
    agentFirstName: null,
    agentLastName: null,
    agentEmail: null,
    agentPhone: null,
    agentLicenseNumber: null,
    agentType: 'mls',
    agentMlsId: null,
    agentMlsOfficeId: null,
    documents: [],
    documentsSubmissionId: null,
    ...current,
    ...partial,
  });
};
